import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";

import { Controller, useFormContext } from "react-hook-form";

import { Paper } from "@mui/material";
import Grid from "@mui/material/Grid";

import { CustomerSelect } from "@/features/customers";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { ReturnableTypeEnum, ReturnableTypeLabel } from "../../returnable-type";
import { StockInModeEnum, stockInModeLabel } from "../../stock-in-mode";
import {
  CustomDatePicker,
  CustomInputField,
  CustomSelect,
} from "@/features/ui";

export const GeneralInformationSection = ({ editMode = false }) => {
  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();

  const seller = watch("seller");
  const buyerableType = watch("buyerableType");
  const returnableType = watch("returnableType");
  const returnableProductVariants = watch("returnableProductVariants");
  const salesReturnExpenses = watch("salesReturnExpenses");
  const stockInMode = watch("stockInMode");

  // Disable buyer and seller when there are existing products
  // BUT only for DELIVERY_ORDER and SALES_INVOICE modes
  // For NON_INVOICE, users manually add products so buyer/seller should remain editable
  const disableBuyerSeller =
    returnableProductVariants.length > 0 &&
    returnableType !== ReturnableTypeEnum.NON_INVOICE;

  // Return source options
  const returnableTypeOptions = useMemo(
    () =>
      Object.values(ReturnableTypeEnum).map((type) => ({
        value: type,
        label: ReturnableTypeLabel[type],
      })),
    []
  );

  // Stock in mode options
  const stockInModeOptions = useMemo(
    () =>
      Object.values(StockInModeEnum).map((mode) => ({
        value: mode,
        label: stockInModeLabel[mode],
      })),
    []
  );

  // Auto-set buyerableType based on seller's role
  useEffect(() => {
    if (seller) {
      const buyerableType =
        seller.role === OutletRoleEnum.RETAIL ? "CUSTOMER" : "OUTLET";
      setValue("buyerableType", buyerableType);
    }
  }, [seller, setValue]);

  useEffect(() => {
    if (editMode) return;

    if (buyerableType) {
      setValue("buyer", null);
    }
  }, [buyerableType, editMode, setValue]);

  // Auto-update product variant stock_in_mode based on main stockInMode
  useEffect(() => {
    if (editMode || !returnableProductVariants.length) return;

    const updatedVariants = returnableProductVariants.map((variant) => {
      if (stockInMode === StockInModeEnum.FULL) {
        return { ...variant, stockInMode: StockInModeEnum.FULL };
      } else if (stockInMode === StockInModeEnum.NONE) {
        return { ...variant, stockInMode: StockInModeEnum.NONE };
      }
      // For PARTIAL, keep the existing stockInMode
      return variant;
    });

    setValue("returnableProductVariants", updatedVariants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockInMode, editMode, setValue]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
          <Controller
            control={control}
            name="buyer"
            render={({ field, fieldState }) =>
              buyerableType === "CUSTOMER" ? (
                <CustomerSelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={editMode || disableBuyerSeller || !buyerableType}
                  TextFieldProps={{
                    label: "Pelanggan (Customer)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              ) : (
                <AllOutletsSelect
                  value={field.value}
                  onChange={field.onChange}
                  allowedRoles={[OutletRoleEnum.RETAIL]}
                  disabled={editMode || disableBuyerSeller || !buyerableType}
                  TextFieldProps={{
                    label: "Pelanggan (Outlet Retail)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="seller"
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || disableBuyerSeller}
                allowedRoles={[OutletRoleEnum.HUB]}
                TextFieldProps={{
                  label: "Outlet",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal Transaksi"
                error={fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  disableFuture: true,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="returnableType"
            render={({ field, fieldState }) => (
              <CustomSelect
                value={field.value}
                onChange={(event) => {
                  const newValue = event.target.value;
                  field.onChange(newValue);
                  // Expenses are bound to the source: invoice lines reference it
                  // directly, and free-form lines are only valid without one.
                  // Neither survives a change of source.
                  setValue("salesReturnExpenses", []);
                  setValue("returnableReferenceNumber", null);
                  if (newValue === ReturnableTypeEnum.NON_INVOICE) {
                    setValue("returnableId", null);
                  } else {
                    setValue("warehouse", null);
                  }
                }}
                options={returnableTypeOptions}
                label="Sumber Retur"
                required
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={
                  returnableProductVariants.length > 0 ||
                  salesReturnExpenses.length > 0
                }
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="stockInMode"
            render={({ field, fieldState }) => (
              <CustomSelect
                value={field.value}
                onChange={field.onChange}
                options={stockInModeOptions}
                label="Pengembalian Barang"
                required
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={returnableType === ReturnableTypeEnum.NON_INVOICE}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <CustomInputField
            name="note"
            label="Catatan"
            register={register}
            error={Boolean(errors.note)}
            helperText={errors.note?.message}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
